import type {CustomHTMLRenderer, PluginContext, PluginInfo} from '@toast-ui/editor'
import type {Node as PmNode} from 'prosemirror-model'
import {EnhancedWorkpace} from '@/types/Workspace'
import {instanceColor, tintClasses} from '@/utils/colors'
import {VALUE_DOT} from '@/types/ColoredOption'
import {fieldRanges, MentionContext, mentionLinkOf, referenceRanges, replaceFields, replaceMentions} from '@/utils/markdownMentions'
import {booleanFace, personName} from '@/utils/propertyDisplay'
import {personAvatar} from '@/utils/personAvatar'

/**
 * Como se desenha cada referência do markdown (ver `markdownMentions`) — pelos recursos do próprio Toast
 * UI, sem remendo: o `customHTMLRenderer` no visualizador e na prévia do editor, e um plugin de
 * decoração (`wysiwygPlugins`) no modo visual.
 *
 * **Um dono para a aparência** (`mentionLook`): as duas portas desenham as mesmas classes, e o CSS
 * (`styles/markdown.css`) é um só.
 *
 * - **Item**: a régua vertical na cor do item, a mesma do campo de relacionamento (`instanceColor`, que
 *   sai do id); apagado, e sem levar a lugar nenhum, o que quem lê não vê ou que não existe mais.
 * - **Coleção**: o ícone de coleção antes do nome.
 * - **Pessoa**: a bolinha pequena e o nome, como o valor de um campo de usuário — a foto, ou as
 *   iniciais sobre a cor dela, como o `UserAvatar` (`personAvatar`) —, sem link (decisão do usuário: só
 *   mostra).
 * - **Sim/não** de um `{{campo}}`: o sinal, a cor e a palavra da grade (`booleanFace`).
 * - **Situação** (`{{_state}}`): a bolinha de semáforo na cor dela; **opção de domínio**: o fundo
 *   tingido — os dois como o `ColoredOptionChip`, com as mesmas classes.
 */
/**
 * `tag`: a pessoa é um `span`, e não um link — ninguém navega para uma pessoa (decisão do usuário), e um
 * `<a href="@joana">` levava o navegador a um endereço que não existe (relato do usuário).
 */
export type MentionLook = {
    classNames: string[], tag: 'a' | 'span', style?: string
    /** O ícone antes do nome, com as classes dele — sem elas, o tom apagado de ícone de coleção. */
    icon?: string, iconClasses?: string[]
    avatar?: ReturnType<typeof personAvatar> & { photo?: string }
}

/** O ícone das coleções — o da aba de coleções do design do ambiente. */
const COLLECTION_ICON = 'mdi-table'

export function mentionLook(href: string, workspace: EnhancedWorkpace): MentionLook | undefined {
    const link = mentionLinkOf(href, workspace.alias)
    if (!link) return undefined
    if (link.kind === 'item') return link.unavailable
        ? {classNames: ['md-mention', 'md-mention--item', 'md-mention--unavailable'], tag: 'span'}
        : {classNames: ['md-mention', 'md-mention--item'], tag: 'a', style: `--md-mention-color: ${instanceColor(link.instance)}`}
    if (link.kind === 'collection') return {classNames: ['md-mention', 'md-mention--collection'], tag: 'a', icon: COLLECTION_ICON}
    if (link.kind === 'boolean') {
        const face = booleanFace(link.value)
        return {classNames: ['md-mention', 'md-mention--boolean', `text-${face.color}`], tag: 'span', icon: face.icon}
    }
    //A situação e a opção como o `ColoredOptionChip` as desenha, com as classes globais dele
    if (link.kind === 'dot') return {classNames: ['md-mention', 'md-mention--dot'], tag: 'span', icon: VALUE_DOT,
        iconClasses: ['option-dot', ...(link.color ? [`text-${link.color}`] : [])]}
    if (link.kind === 'chip') return {classNames: ['md-mention', 'md-mention--chip', 'option-chip-text', ...tintClasses(link.color)], tag: 'span'}
    const person = workspace.people.find(candidate => candidate.alias === link.alias)
    return {classNames: ['md-mention', 'md-mention--person'], tag: 'span',
        avatar: person && {...personAvatar(person.user, personName(person)), photo: person.photo}}
}

const attribute = (text: string) => text.replace(/[&"<>]/g, char => `&#${char.charCodeAt(0)};`)

/**
 * O clique num link do markdown desenhado — o visualizador e o resumo linearizado: o item e a coleção
 * navegam pelo roteador, sem recarregar a página (com Ctrl, Shift ou o botão do meio, o link é de
 * verdade e abre outra aba); o link de fora abre sozinho. Nos dois, o clique não sobe: numa linha ou
 * num card, ele abriria o item de lá também.
 *
 * Pelo endereço do link, e não por um atributo marcador: o filtro de HTML do Toast UI pode tirar o que
 * não conhece.
 */
export function followLink(event: MouseEvent, workspace: EnhancedWorkpace | null, push: (href: string) => unknown) {
    const anchor = (event.target as HTMLElement).closest('a') as HTMLAnchorElement | null
    if (!anchor) return
    event.stopPropagation()
    const href = anchor.getAttribute('href') || ''
    //Navega o que se desenha como link: quem decide é o `mentionLook`
    const look = workspace && mentionLook(href, workspace)
    if (!look || look.tag !== 'a') return
    if (event.ctrlKey || event.metaKey || event.shiftKey) return
    event.preventDefault()
    push(href)
}

/**
 * As referências cruas (`[[cidade#1]]`) trocadas pelo link desenhado, no HTML da prévia do modo Markdown
 * do editor — lá o texto mostra a referência como se grava (pedido do usuário), e é a prévia ao lado que
 * a desenha. Pelo gancho `beforePreviewRender` do Toast UI, com o mesmo desenho do `mentionRenderer`.
 *
 * E o `{{campo}}` no valor, com o item como está no formulário: a prévia mostra o texto como ele vai
 * aparecer (pedido do usuário). No texto do editor ele fica como se escreveu — é o modelo, e trocado
 * gravaria o valor no lugar dele. O item de um relacionamento e a pessoa saem como as referências.
 */
export function mentionsInHtml(html: string, context: MentionContext): string {
    const span = (href: string, label: string) => mentionSpan(href, label, context.workspace)
    return replaceFields(replaceMentions(html, context, span), context, attribute, span)
}

function mentionSpan(href: string, label: string, workspace: EnhancedWorkpace): string {
    const look = mentionLook(href, workspace)
    const style = look && look.style ? ` style="${attribute(look.style)}"` : ''
    //Um `span`, e não link: clicar na prévia levaria para fora do formulário no meio da edição
    return `<span class="${(look ? look.classNames : []).join(' ')}"${style}>${look ? lookPrefix(look) : ''}${attribute(label)}</span>`
}

/**
 * A bolinha da pessoa e o ícone da coleção, antes do nome — um dono para o visualizador, a prévia, o modo
 * visual e o resumo. A bolinha como a do `UserAvatar`: as iniciais sempre desenhadas, sobre a cor da
 * pessoa, e a foto por cima, quando há — a que não pinta deixa as iniciais.
 */
export function lookPrefix(look: MentionLook): string {
    const {avatar, icon, iconClasses = ['md-mention-icon']} = look
    return (avatar ? `<span class="md-mention-avatar ${avatar.classes.join(' ')}">${attribute(avatar.initials)}`
            + (avatar.photo ? `<img src="${attribute(avatar.photo)}" alt="">` : '') + '</span>' : '')
        + (icon ? `<i class="mdi ${icon} ${iconClasses.join(' ')}" aria-hidden="true"></i>` : '')
}

/** O link de referência no visualizador e na prévia: as classes, a cor e, na pessoa, a foto. */
export function mentionRenderer(workspace: EnhancedWorkpace): CustomHTMLRenderer {
    return {
        link(node, {entering, origin}) {
            const href = (node as unknown as { destination?: string }).destination || ''
            const look = mentionLook(href, workspace)
            if (!look) return origin!()
            if (!entering) return {type: 'closeTag', tagName: look.tag}
            return [
                {type: 'openTag', tagName: look.tag, classNames: look.classNames,
                    attributes: {...(look.tag === 'a' ? {href} : {}), ...(look.style ? {style: look.style} : {})}},
                ...(look.avatar || look.icon ? [{type: 'html' as const, content: lookPrefix(look)}] : [])
            ]
        }
    }
}

/** Um elemento a partir do HTML que a prévia já desenha — o widget do modo visual. */
function htmlElement(html: string, className: string): HTMLElement {
    const element = document.createElement('span')
    element.className = className
    element.contentEditable = 'false'
    element.innerHTML = html
    return element
}

/** O valor de um `{{campo}}` desenhado, como na prévia; o campo que não há, nada — fica como se escreveu. */
function fieldHtml(token: string, context: MentionContext): string | undefined {
    const html = mentionsInHtml(token, context)
    return html === token ? undefined : html
}

type Piece = { from: number, to: number }

/**
 * A peça do modo visual perto do cursor em `position` — o `{{campo}}` que tem valor, ou o link de uma
 * referência (item, coleção, pessoa): a que o contém (`inside`), a que acaba nele (`before`, a do
 * Backspace) ou a que começa nele (`after`, a do Delete).
 */
function pieceNear(doc: PmNode, position: number, where: 'inside' | 'before' | 'after',
                   context: MentionContext): Piece | undefined {
    const $position = doc.resolve(position)
    const parent = $position.parent
    const {node, index, offset} = where === 'before' ? parent.childBefore($position.parentOffset)
        : parent.childAfter($position.parentOffset)
    if (!node || !node.isText) return undefined
    const pieces = [...fieldPieces(node, $position.start() + offset, context),
        ...linkPieces(parent, index, $position.start(), context)]
    return pieces.find(({from, to}) =>
        where === 'inside' ? from < position && position < to : where === 'before' ? to === position : from === position)
}

/** Os `{{campo}}` com valor de um texto que começa em `start`. */
function fieldPieces(node: PmNode, start: number, context: MentionContext): Piece[] {
    return fieldRanges(node.text || '')
        .filter(([from, to]) => fieldHtml(node.text!.slice(from, to), context))
        .map(([from, to]) => ({from: start + from, to: start + to}))
}

/** O link de referência de que o texto `index` faz parte, inteiro — com negrito no meio, é mais de um texto. */
function linkPieces(parent: PmNode, index: number, parentStart: number, context: MentionContext): Piece[] {
    const link = parent.child(index).marks.find(mark => mark.type.name === 'link')
    if (!link || !mentionLook(String(link.attrs.linkUrl || ''), context.workspace)) return []
    let first = index
    let last = index
    while (first > 0 && link.isInSet(parent.child(first - 1).marks)) first--
    while (last < parent.childCount - 1 && link.isInSet(parent.child(last + 1).marks)) last++
    const sizeUntil = (end: number) => Array.from({length: end}, (_, i) => parent.child(i).nodeSize).reduce((a, b) => a + b, 0)
    return [{from: parentStart + sizeUntil(first), to: parentStart + sizeUntil(last + 1)}]
}

/**
 * O mesmo desenho no modo visual do editor, onde o link é uma marca do ProseMirror e não passa pelo
 * renderizador: uma decoração com as classes e a cor sobre o texto de cada link de referência, e a foto
 * da pessoa (o ícone da coleção) num widget antes do nome.
 *
 * E o `{{campo}}` como o valor dele, como na prévia (pedido do usuário: o que se vê é o resultado — é o
 * WYSIWYG). O texto continua `{{campo}}` — é ele que se grava, e o valor acompanha o item —, escondido
 * por uma decoração, com o valor desenhado num widget por cima. O valor se refaz a cada alteração do
 * texto, como a prévia.
 *
 * O campo e o link de referência são uma peça só (`pieceNear`): o cursor os pula, e o Backspace ou o
 * Delete os apagam inteiros. O nome do link não se edita — é o de agora, e ao gravar só a referência
 * fica (relato do usuário: apagava letras do nome da pessoa). Para trocar, apagar e inserir de novo.
 *
 * No modo Markdown, as referências cruas (`[[cidade#1]]`, `{{prazo}}`) na fonte monoespaçada do próprio
 * editor (pedido do usuário): a classe do bloco de código do Toast UI, que só dá a fonte — sem copiar a
 * lista de fontes dele para cá.
 */
export function mentionDecorations(mentionContext: () => MentionContext): (context: PluginContext) => PluginInfo {
    return (context: PluginContext) => {
        const {Decoration, DecorationSet} = context.pmView
        type Decorated = ReturnType<typeof Decoration.inline>
        //Um plugin que decora cada texto do documento com o que `decorate` achar nele
        const decorating = (decorate: (node: PmNode, position: number, parent: PmNode, index: number) => Decorated[]) =>
            () => new context.pmState.Plugin({
                props: {
                    decorations(state) {
                        const decorations: Decorated[] = []
                        state.doc.descendants((node: PmNode, position: number, parent: PmNode | null, index: number) => {
                            if (node.isText) decorations.push(...decorate(node, position, parent!, index))
                        })
                        return DecorationSet.create(state.doc, decorations)
                    }
                }
            })

        const links = decorating((node, position, parent, index) => {
            const link = node.marks.find(mark => mark.type.name === 'link')
            const look = link && mentionLook(String(link.attrs.linkUrl || ''), mentionContext().workspace)
            if (!link || !look) return []
            const decorations = [Decoration.inline(position, position + node.nodeSize,
                {class: look.classNames.join(' '), ...(look.style ? {style: look.style} : {})})]
            //A foto e o ícone só no primeiro pedaço: um link com negrito no meio é mais de um texto
            const first = !index || !link.isInSet(parent.child(index - 1).marks)
            const prefix = lookPrefix(look)
            //Do lado de depois, como o valor do campo: o cursor no início do link fica antes da foto
            if (first && prefix) decorations.push(Decoration.widget(position, () => htmlElement(prefix, 'md-mention-prefix'),
                {side: 1, key: prefix}))
            return decorations
        })

        const fields = decorating((node, position) => fieldRanges(node.text || '').flatMap(([from, to]) => {
            const html = fieldHtml(node.text!.slice(from, to), mentionContext())
            return html ? [
                Decoration.inline(position + from, position + to, {class: 'md-field-source'}),
                //Do lado de depois do início: o cursor no início fica antes do valor, e no fim, depois
                Decoration.widget(position + from, () => htmlElement(html, 'md-field-value'), {side: 1, key: html})
            ] : []
        }))

        const atomicPieces = () => new context.pmState.Plugin({
            //O cursor que caiu dentro da peça — pela seta, pelo clique — vai para a ponta do lado de onde veio
            appendTransaction(transactions, oldState, newState) {
                const {selection} = newState
                const piece = selection.empty && pieceNear(newState.doc, selection.from, 'inside', mentionContext())
                if (!piece) return null
                const leftward = selection.from < oldState.selection.from
                return newState.tr.setSelection(context.pmState.TextSelection.create(newState.doc, leftward ? piece.from : piece.to))
            },
            props: {
                handleKeyDown(view, event) {
                    if ((event.key !== 'Backspace' && event.key !== 'Delete') || !view.state.selection.empty) return false
                    const piece = pieceNear(view.state.doc, view.state.selection.from,
                        event.key === 'Backspace' ? 'before' : 'after', mentionContext())
                    if (!piece) return false
                    view.dispatch(view.state.tr.delete(piece.from, piece.to))
                    return true
                }
            }
        })

        return {
            wysiwygPlugins: [links, fields, atomicPieces],
            markdownPlugins: [decorating((node, position) => referenceRanges(node.text || '')
                .map(([from, to]) => Decoration.inline(position + from, position + to,
                    {class: 'toastui-editor-md-code-block'})))]
        }
    }
}
